/**
 * Chart colour parameters for the admin dashboard.
 *
 * The categorical slots are the first six of the validated default order
 * (blue, orange, aqua, yellow, magenta, green). Validated on a white card
 * surface: lightness band PASS, chroma floor PASS, worst adjacent CVD ΔE 9.1,
 * worst adjacent normal-vision ΔE 19.6. Three slots fall below 3:1 contrast, so
 * every chart using them ships **direct labels and a table view** — that relief
 * is not optional. Assign in this fixed order, never cycled: a seventh obra
 * folds into "Outras" rather than inventing a hue.
 */
export const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'] as const;

/** How many obras get their own colour before the rest collapse into "Outras". */
export const MAX_SERIES = SERIES.length - 1;

/** The colour for the folded "Outras" bucket — deliberately outside the ramp. */
export const OUTRAS = '#9aa0ae';

/** Brand navy: the single-series colour for magnitude-only charts. */
export const NAVY = '#14257d';

/** Recessive grid/axis ink. */
export const GRID = '#eef0f5';
export const AXIS_TEXT = '#89909f';

/**
 * Sequential blue ramp, light → dark, for the completeness heatmap. Step 0 is
 * the "logged nothing" surface tint; magnitude starts at index 1.
 */
export const SEQUENTIAL = ['#eef2f9', '#cde2fb', '#9ec5f4', '#5598e7', '#2a78d6', '#184f95'] as const;

/** Picks a sequential step for a value in [0, max]. */
export function sequentialStep(value: number, max: number): string {
  if (value <= 0) return SEQUENTIAL[0];
  const ratio = max > 0 ? value / max : 0;
  const index = Math.min(SEQUENTIAL.length - 1, 1 + Math.floor(ratio * (SEQUENTIAL.length - 2)));
  return SEQUENTIAL[index];
}

/** Stable colour per obra id, assigned by rank once and reused everywhere on the page. */
export function buildObraColors(obraIds: string[]): Map<string, string> {
  const map = new Map<string, string>();
  obraIds.slice(0, MAX_SERIES).forEach((id, i) => map.set(id, SERIES[i]));
  return map;
}
